import { type ComponentPropsWithoutRef, type ComponentRef, forwardRef } from 'react'

import * as SheetPrimitive from '@radix-ui/react-dialog'
import { cva, type VariantProps } from 'class-variance-authority'
import { motion } from 'motion/react'

import { cn } from '@/lib/utils'

export const Sheet = SheetPrimitive.Root

export const SheetTrigger = SheetPrimitive.Trigger

export const SheetClose = SheetPrimitive.Close

export const SheetPortal = SheetPrimitive.Portal

export const SheetOverlay = forwardRef<
  ComponentRef<typeof SheetPrimitive.Overlay>,
  ComponentPropsWithoutRef<typeof SheetPrimitive.Overlay>
>(({ className, ...props }, ref) => (
  <SheetPrimitive.Overlay className={cn('fixed inset-0 z-50', className)} {...props} ref={ref} />
))

const sheetVariants = cva('fixed z-50 gap-4', {
  variants: {
    side: {
      top: 'inset-x-0 top-0',
      bottom: 'inset-x-0 bottom-0',
      left: 'inset-y-0 left-0 h-full w-1/4',
      right: 'inset-y-0 right-0 h-full w-1/4',
    },
  },
  defaultVariants: {
    side: 'right',
  },
})

export interface SheetContentProps
  extends ComponentPropsWithoutRef<typeof SheetPrimitive.Content>, VariantProps<typeof sheetVariants> {
  overlayClassName?: string
}

export const SheetContent = forwardRef<ComponentRef<typeof SheetPrimitive.Content>, SheetContentProps>(
  ({ side = 'right', className, overlayClassName, children, ...props }, ref) => {
    const variants = {
      open: {
        x: 0,
        transition: {
          duration: 0.2,
          ease: 'easeOut',
        },
      },
      closed: {
        x: side === 'right' ? '100%' : side === 'left' ? '-100%' : 0,
        transition: {
          duration: 0.2,
          ease: 'easeIn',
        },
      },
    } as const

    return (
      <SheetPortal>
        <SheetOverlay className={overlayClassName} />
        <SheetPrimitive.Content ref={ref} className={cn(sheetVariants({ side }), className)} {...props}>
          <motion.div
            initial="closed"
            animate="open"
            exit="closed"
            variants={variants}
            className="size-full overflow-y-auto border-l border-gray-200 bg-white shadow-md">
            <SheetPrimitive.Title />
            <SheetPrimitive.Description />
            {children}
          </motion.div>
        </SheetPrimitive.Content>
      </SheetPortal>
    )
  }
)
